import { InboxOutlined } from '@ant-design/icons';
import { Alert, App, Button, Input, Modal, Space, Table, Tabs, Upload } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PickOption, searchUserOptions } from 'components/add-members-modal';
import DebounceSelect from 'components/debounce-select/select';
import { addGuestsFromUsers, importGuestImages, importGuestSheet, pasteGuests } from '../api';
import { GUEST_GROUP_PRESETS, UpsertResult, errorText } from '../contract';
import { filesWithoutUid, previewPaste, uidFromFilename } from '../lib/paste-preview';
import styles from '../styles.module.scss';

const { TextArea } = Input;

/** Giới hạn của server (IMAGE_BATCH_MAX, guestSheetUploadOptions). */
const MAX_IMAGES = 50;
const MAX_SHEET_BYTES = 5 * 1024 * 1024;

interface Props {
  eventId: number;
  open: boolean;
  onClose: () => void;
  /** Đã thêm xong một đợt — nơi gọi tải lại danh sách vé. */
  onDone: () => void | Promise<void>;
}

/** Bốn con số của một đợt thêm + bảng lỗi từng dòng. */
const ResultBox = ({ result, note }: { result: UpsertResult; note?: string }) => (
  <div style={{ marginTop: 16 }}>
    <Alert
      showIcon
      type={result.errors.length ? 'warning' : 'success'}
      message={`Mới ${result.created} · Cập nhật ${result.updated} · Đã khớp tài khoản ${result.matched} · Chờ gắn tài khoản ${result.pending}`}
      description={
        [
          result.errors.length ? `${result.errors.length} dòng bị bỏ qua, xem bảng bên dưới.` : '',
          note ?? '',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
    />
    {result.errors.length > 0 && (
      <Table
        style={{ marginTop: 8 }}
        size="small"
        rowKey={(r) => `${r.line}-${r.reason}`}
        dataSource={result.errors}
        pagination={result.errors.length > 10 ? { pageSize: 10, size: 'small' } : false}
        columns={[
          { title: 'Dòng', dataIndex: 'line', width: 70 },
          { title: 'Lý do', dataIndex: 'reason' },
        ]}
      />
    )}
  </div>
);

/** Modal "Thêm khách mời": dán / Excel / ảnh vé / tài khoản (SPEC mục 4.4). */
const AddGuestsModal = ({ eventId, open, onClose, onDone }: Props) => {
  const { message } = App.useApp();
  const [tab, setTab] = useState('paste');
  const [text, setText] = useState('');
  const [sheet, setSheet] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const imagesRef = useRef<File[]>([]);
  const [users, setUsers] = useState<PickOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<UpsertResult | null>(null);
  const [note, setNote] = useState<string | undefined>();

  // Mở lại modal: bắt đầu sạch.
  useEffect(() => {
    if (!open) return;
    setTab('paste');
    setText('');
    setSheet(null);
    imagesRef.current = [];
    setImages([]);
    setUsers([]);
    setResult(null);
    setNote(undefined);
  }, [open]);

  const paste = useMemo(() => previewPaste(text), [text]);
  const badImageNames = useMemo(() => new Set(filesWithoutUid(images.map((f) => f.name))), [images]);
  const goodImages = useMemo(() => images.filter((f) => !badImageNames.has(f.name)), [images, badImageNames]);

  /** Chạy một đợt thêm; trả true khi thành công (để nơi gọi dọn ô nhập). */
  const run = async (
    task: () => Promise<UpsertResult>,
    fail: string,
    extraNote?: string
  ): Promise<boolean> => {
    setBusy(true);
    try {
      const res = await task();
      setResult(res);
      setNote(extraNote);
      await onDone();
      return true;
    } catch (e) {
      message.error(errorText(e, fail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // antd gọi beforeUpload lần lượt cho từng file trong CÙNG một lượt chọn, trước khi React kịp render lại,
  // nên danh sách hiện hành giữ trong ref (cập nhật ngay) để đếm đủ giới hạn 50; cảnh báo nằm ngoài setState.
  const addImages = (file: File) => {
    const current = imagesRef.current;
    if (current.some((f) => f.name === file.name && f.size === file.size)) return;
    if (current.length >= MAX_IMAGES) {
      message.warning(`Mỗi lần chỉ gửi tối đa ${MAX_IMAGES} ảnh.`);
      return;
    }
    imagesRef.current = [...current, file];
    setImages(imagesRef.current);
  };

  const clearImages = () => {
    imagesRef.current = [];
    setImages([]);
  };

  const tabs = [
    {
      key: 'paste',
      label: 'Dán danh sách',
      children: (
        <>
          <p className={styles.hint}>
            Mỗi dòng một khách: <code>UID, tên trên vé, nhóm, ghi chú</code> (cách nhau bằng dấu phẩy,
            tab hoặc <code>|</code>). Chỉ UID là bắt buộc — UID TikTok gồm 15–20 chữ số. Nhóm có sẵn:{' '}
            {GUEST_GROUP_PRESETS.join(', ')} (chữ thường tự đổi sang in hoa).
          </p>
          <TextArea
            rows={9}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'7381234567890123456, Nguyễn Văn A, VIP, Bàn 1\n7381234567890123457'}
            aria-label="Danh sách khách mời"
          />
          <div style={{ marginTop: 8 }}>
            <strong>
              Nhận diện {paste.valid}/{paste.total} dòng
            </strong>
            {paste.invalid.length > 0 && (
              <span className={styles.unlinked}>
                {' '}
                · Dòng không có UID: {paste.invalid.slice(0, 12).join(', ')}
                {paste.invalid.length > 12 ? '…' : ''}
              </span>
            )}
          </div>
          <Button
            type="primary"
            style={{ marginTop: 12 }}
            loading={busy}
            disabled={paste.total === 0}
            onClick={() => run(() => pasteGuests(eventId, text), 'Không thêm được danh sách.')}
          >
            Thêm vào danh sách
          </Button>
        </>
      ),
    },
    {
      key: 'excel',
      label: 'File Excel / CSV',
      children: (
        <>
          <p className={styles.hint}>
            Dòng đầu là tiêu đề. Nhận cột <code>UID</code> (hoặc <code>TikTok UID</code>),{' '}
            <code>Tên</code> (hoặc <code>Tên trên vé</code>), <code>Nhóm</code>, <code>Ghi chú</code>.
            Hãy định dạng cột UID là Text để Excel không làm tròn số. Tối đa 5 MB.
          </p>
          <Upload.Dragger
            accept=".xlsx,.csv"
            maxCount={1}
            fileList={sheet ? [{ uid: 'sheet', name: sheet.name, status: 'done' }] : []}
            beforeUpload={(file) => {
              if (file.size > MAX_SHEET_BYTES) {
                message.warning('File quá 5 MB.');
              } else {
                setSheet(file as File);
              }
              return false;
            }}
            onRemove={() => setSheet(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Bấm hoặc kéo file .xlsx / .csv vào đây</p>
          </Upload.Dragger>
          <Button
            type="primary"
            style={{ marginTop: 12 }}
            loading={busy}
            disabled={!sheet}
            onClick={() =>
              sheet && run(() => importGuestSheet(eventId, sheet), 'Không nhập được file danh sách.')
            }
          >
            Nhập file
          </Button>
        </>
      ),
    },
    {
      key: 'images',
      label: 'Ảnh vé làm sẵn',
      children: (
        <>
          <p className={styles.hint}>
            Đặt tên file theo UID: <code>&lt;UID&gt;.png</code> hoặc <code>&lt;UID&gt;-ghichu.jpg</code>{' '}
            (png, jpg, webp). Tối đa {MAX_IMAGES} ảnh mỗi lần. File không có UID ở đầu tên sẽ bị bỏ qua.
          </p>
          <Upload.Dragger
            multiple
            accept="image/png,image/jpeg,image/webp"
            showUploadList={false}
            beforeUpload={(file) => {
              addImages(file as File);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Bấm hoặc kéo nhiều ảnh vé vào đây</p>
          </Upload.Dragger>
          {images.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>
                {goodImages.length}/{images.length} ảnh có UID trong tên
              </strong>
              <ul style={{ maxHeight: 180, overflow: 'auto', margin: '4px 0 0', paddingLeft: 18 }}>
                {images.map((f) => {
                  const uid = uidFromFilename(f.name);
                  return (
                    <li key={`${f.name}-${f.size}`} className={uid ? undefined : styles.fileBad}>
                      {f.name}
                      {uid ? ` → ${uid}` : ' — không có UID, sẽ bị bỏ qua'}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <Space style={{ marginTop: 12 }}>
            <Button
              type="primary"
              loading={busy}
              disabled={goodImages.length === 0}
              onClick={() =>
                run(
                  () => importGuestImages(eventId, goodImages),
                  'Không tải được ảnh vé.',
                  badImageNames.size
                    ? `Đã bỏ qua ${badImageNames.size} file không có UID trong tên.`
                    : undefined
                )
              }
            >
              Gửi {goodImages.length} ảnh
            </Button>
            {images.length > 0 && <Button onClick={clearImages}>Xoá danh sách ảnh</Button>}
          </Space>
        </>
      ),
    },
    {
      key: 'users',
      label: 'Chọn tài khoản',
      children: (
        <>
          <p className={styles.hint}>
            Tìm theo tên, @username, email hoặc UID TikTok (từ 2 ký tự). Tài khoản được gắn vé ngay.
          </p>
          <DebounceSelect
            mode="multiple"
            value={users as any}
            fetchOptions={searchUserOptions}
            onChange={(v: any) => setUsers(v as PickOption[])}
            style={{ width: '100%' }}
            placeholder="Gõ để tìm tài khoản…"
            aria-label="Tìm tài khoản"
          />
          <Button
            type="primary"
            style={{ marginTop: 12 }}
            loading={busy}
            disabled={users.length === 0}
            onClick={() =>
              run(
                () => addGuestsFromUsers(eventId, users.map((u) => Number(u.value))),
                'Không thêm được tài khoản.'
              ).then((ok) => {
                if (ok) setUsers([]);
              })
            }
          >
            Thêm {users.length || ''} tài khoản
          </Button>
        </>
      ),
    },
  ];

  return (
    <Modal
      title="Thêm khách mời"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      width={720}
      destroyOnClose
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          setTab(k);
          setResult(null);
        }}
        items={tabs}
      />
      {result && <ResultBox result={result} note={note} />}
    </Modal>
  );
};

export default AddGuestsModal;
